// Importar / exportar planilha de sugestões de tema e de cards (out/26, João):
// antes cada planejamento de cliente era carregado à mão por script
// (ex.: emiteai/social/carga-novembro/build.cjs). Aqui ficam as regras que o
// time da Raz usa pela tela: campos aceitos, cabeçalhos reconhecidos, leitura
// de data/funil/rede/formato/etapa, o que vai para Observações e a validação
// linha a linha. Funções puras: o front usa para montar o modelo e o servidor
// repete TODA a validação antes de gravar (nunca confia no que veio do front).

import { COLUNAS_PAUTA_BANCO, ETAPA_SUGESTAO, etapasDePauta, etapaInicial, rotuloEtapa, type EtapaDef } from "./etapas";

export type TipoImportacao = "temas" | "pautas";

export const LIMITE_LINHAS = 500;
// Limites do que a tela manda ao servidor (o servidor confere os mesmos).
export const LIMITE_COLUNAS = 120;
export const LIMITE_CABECALHO = 500;      // caracteres por cabeçalho
export const LIMITE_CELULA = 200_000;     // caracteres por célula
// TEXT do MariaDB: 65.535 bytes.
export const LIMITE_TEXT_BYTES = 65535;

export const NOME_TIPO: Record<TipoImportacao, string> = {
  temas: "Sugestões de tema",
  pautas: "Cards",
};

// Nome da aba de dados no modelo (a importação procura por ela primeiro).
export const ABA_DADOS: Record<TipoImportacao, string> = {
  temas: "Sugestões de tema",
  pautas: "Cards",
};
export const ABA_INSTRUCOES = "Instruções";
export const ABA_LISTAS = "Listas";
export const ABA_EXISTENTES = "Já no sistema";
// Abas do modelo que a importação nunca lê.
export const ABAS_NAO_LIDAS = [ABA_INSTRUCOES, ABA_LISTAS, ABA_EXISTENTES];

export type TipoCampo =
  | "codigo" | "titulo" | "texto" | "data" | "funil" | "rede" | "formato"
  | "etapa" | "canais" | "mae";

export interface CampoDef {
  key: string;          // nome do campo (o mesmo do tRPC)
  label: string;        // cabeçalho no modelo
  aliases: string[];    // outros cabeçalhos aceitos
  tipo: TipoCampo;
  max?: number;         // limite em caracteres (VARCHAR)
  textoLongo?: boolean; // TEXT: limite em bytes
  link?: boolean;       // na planilha, vale o endereço do hiperlink da célula
  obrigatorio?: boolean;
  padrao?: boolean;     // vem marcado no modelo
  grupo: "Identificação" | "Planejamento" | "Classificação" | "Conteúdo" | "Peça relacionada" | "Links";
  ajuda: string;        // o que pôr (aba de instruções)
  exemplo: string;
}

const CAMPOS_TEMA: CampoDef[] = [
  { key: "codigo", label: "Código", aliases: ["codigo", "cod", "id", "id da peca", "codigo da peca", "content id"], tipo: "codigo", max: 20, padrao: true, grupo: "Identificação",
    ajuda: "Código único da peça. Liga a sugestão ao card quando ela for aprovada e é por ele que o comentário ou o story aponta para a peça-mãe.", exemplo: "EXEMPLO" },
  { key: "tema", label: "Tema", aliases: ["tema", "titulo", "título", "assunto", "pauta"], tipo: "titulo", max: 1000, obrigatorio: true, padrao: true, grupo: "Identificação",
    ajuda: "O título da sugestão.", exemplo: "[EXEMPLO] apague esta linha. Como conferir o documento antes de liberar a carga" },
  { key: "dataPub", label: "Data de publicação", aliases: ["data", "data pub", "data publicacao", "data de publicacao", "data prevista", "dia"], tipo: "data", padrao: true, grupo: "Planejamento",
    ajuda: "Dia em que a peça vai ao ar.", exemplo: "05/11/2026" },
  { key: "semana", label: "Semana", aliases: ["semana"], tipo: "texto", max: 30, padrao: true, grupo: "Planejamento",
    ajuda: "Texto livre para agrupar por semana.", exemplo: "S1 · 05 e 06/11" },
  { key: "canais", label: "Canais", aliases: ["canais", "canal", "rede", "redes", "rede social"], tipo: "canais", padrao: true, grupo: "Planejamento",
    ajuda: "Onde a peça sai. Mais de um canal: separe por vírgula.", exemplo: "Instagram Feed" },
  { key: "formato", label: "Formato", aliases: ["formato", "tipo de formato"], tipo: "texto", max: 150, padrao: true, grupo: "Planejamento",
    ajuda: "Texto livre. Quando aprovada, a sugestão vira card de comentário se o formato tiver a palavra Comentário; carrossel, reels, stories, vídeo, thread e post também são reconhecidos.", exemplo: "Carrossel" },
  { key: "responsavel", label: "Responsável", aliases: ["responsavel", "dono", "owner"], tipo: "texto", max: 200, padrao: true, grupo: "Planejamento",
    ajuda: "Quem cuida da peça.", exemplo: "Raz" },
  { key: "linhaEditorial", label: "Linha editorial", aliases: ["linha editorial", "linha", "pilar"], tipo: "texto", max: 255, padrao: true, grupo: "Classificação",
    ajuda: "Linha editorial (pilar).", exemplo: "Educação de Mercado" },
  { key: "territorio", label: "Território", aliases: ["territorio", "territorio tematico"], tipo: "texto", max: 120, padrao: true, grupo: "Classificação",
    ajuda: "Território temático.", exemplo: "" },
  { key: "segmento", label: "Segmento", aliases: ["segmento"], tipo: "texto", max: 50, padrao: true, grupo: "Classificação",
    ajuda: "Segmento do público.", exemplo: "" },
  { key: "persona", label: "Persona", aliases: ["persona", "persona principal"], tipo: "texto", max: 50, padrao: true, grupo: "Classificação",
    ajuda: "Persona principal.", exemplo: "Analista" },
  { key: "funil", label: "Funil", aliases: ["funil", "etapa do funil"], tipo: "funil", padrao: true, grupo: "Classificação",
    ajuda: "T, M ou F (Topo, Meio ou Fundo).", exemplo: "T (Topo)" },
  { key: "objetivo", label: "Objetivo", aliases: ["objetivo"], tipo: "texto", textoLongo: true, padrao: true, grupo: "Conteúdo",
    ajuda: "Para que serve a peça.", exemplo: "Mostrar o passo que evita retrabalho." },
  { key: "direcionamento", label: "Direcionamento", aliases: ["direcionamento", "direcionamento do conteudo", "briefing"], tipo: "texto", textoLongo: true, padrao: true, grupo: "Conteúdo",
    ajuda: "O que a peça diz, como abre e como fecha.", exemplo: "Abre com a dúvida mais comum e fecha com o passo a passo." },
  { key: "linkReferencia", label: "Link de referência", aliases: ["link de referencia", "link referencia", "referencia", "link"], tipo: "texto", textoLongo: true, link: true, grupo: "Conteúdo",
    ajuda: "Link de apoio para quem vai produzir.", exemplo: "" },
  { key: "observacoes", label: "Observações", aliases: ["observacoes", "observacao", "obs", "notas"], tipo: "texto", textoLongo: true, padrao: true, grupo: "Conteúdo",
    ajuda: "Texto livre. As colunas a mais da planilha entram aqui, depois deste texto.", exemplo: "" },
  { key: "parentCodigo", label: "Código da peça-mãe", aliases: ["codigo da peca mae", "peca mae", "codigo da mae", "mae", "parent codigo", "parent", "vinculo"], tipo: "mae", max: 20, padrao: true, grupo: "Peça relacionada",
    ajuda: "Só para comentário de porta-voz ou story ligado a outra peça: o código da peça principal. A mãe pode estar nesta planilha ou já existir no Social Hub.", exemplo: "" },
  { key: "portaVoz", label: "Porta-voz", aliases: ["porta voz", "portavoz"], tipo: "texto", max: 200, padrao: true, grupo: "Peça relacionada",
    ajuda: "Só para comentário de porta-voz: quem assina.", exemplo: "" },
  { key: "anguloComentario", label: "Ângulo do comentário", aliases: ["angulo do comentario", "angulo comentario", "angulo"], tipo: "texto", textoLongo: true, padrao: true, grupo: "Peça relacionada",
    ajuda: "Só para comentário de porta-voz: o que ele vai comentar.", exemplo: "" },
];

const CAMPOS_PAUTA: CampoDef[] = [
  { key: "codigo", label: "Código", aliases: ["codigo", "cod", "id", "id da peca", "codigo da peca", "content id"], tipo: "codigo", max: 20, padrao: true, grupo: "Identificação",
    ajuda: "Código único do card (aparece como ID). Vazio: o sistema gera quando o cliente usa ID automático. É por ele que o comentário ou o story aponta para a peça-mãe.", exemplo: "EXEMPLO" },
  { key: "titulo", label: "Título", aliases: ["titulo", "tema", "assunto", "pauta"], tipo: "titulo", max: 255, obrigatorio: true, padrao: true, grupo: "Identificação",
    ajuda: "O título do card.", exemplo: "[EXEMPLO] apague esta linha. Como conferir o documento antes de liberar a carga" },
  { key: "coluna", label: "Etapa", aliases: ["etapa", "coluna", "status", "fase"], tipo: "etapa", padrao: true, grupo: "Planejamento",
    ajuda: "Em que etapa do quadro o card entra.", exemplo: "" },
  { key: "rede", label: "Rede", aliases: ["rede", "rede social", "canal"], tipo: "rede", padrao: true, grupo: "Planejamento",
    ajuda: "Instagram, LinkedIn, TikTok, YouTube, Twitter/X ou Múltiplas. Vazio: Instagram.", exemplo: "Instagram" },
  { key: "formato", label: "Formato", aliases: ["formato"], tipo: "formato", padrao: true, grupo: "Planejamento",
    ajuda: "Feed, Reels, Stories, Carrossel, Vídeo, Post, Thread ou Comentário. Vazio: Feed.", exemplo: "Carrossel" },
  { key: "dataPrevista", label: "Data prevista", aliases: ["data prevista", "data", "data pub", "data de publicacao", "data publicacao", "dia"], tipo: "data", padrao: true, grupo: "Planejamento",
    ajuda: "Dia em que o card vai ao ar. É a data do calendário.", exemplo: "05/11/2026" },
  { key: "horarioPostagem", label: "Horário de postagem", aliases: ["horario de postagem", "horario", "horario de publicacao", "hora"], tipo: "texto", max: 120, padrao: true, grupo: "Planejamento",
    ajuda: "Texto livre.", exemplo: "14h às 17h" },
  { key: "responsavel", label: "Responsável", aliases: ["responsavel", "dono", "owner"], tipo: "texto", max: 100, padrao: true, grupo: "Planejamento",
    ajuda: "Quem cuida do card.", exemplo: "Raz" },
  { key: "linhaEditorial", label: "Linha editorial", aliases: ["linha editorial", "linha", "pilar"], tipo: "texto", max: 255, padrao: true, grupo: "Classificação",
    ajuda: "Linha editorial (pilar).", exemplo: "Educação de Mercado" },
  { key: "territorio", label: "Território", aliases: ["territorio", "territorio tematico"], tipo: "texto", max: 120, padrao: true, grupo: "Classificação",
    ajuda: "Território temático.", exemplo: "" },
  { key: "segmento", label: "Segmento", aliases: ["segmento"], tipo: "texto", max: 50, padrao: true, grupo: "Classificação",
    ajuda: "Segmento do público.", exemplo: "" },
  { key: "persona", label: "Persona", aliases: ["persona", "persona principal"], tipo: "texto", max: 50, padrao: true, grupo: "Classificação",
    ajuda: "Persona principal.", exemplo: "Analista" },
  { key: "funil", label: "Funil", aliases: ["funil", "etapa do funil"], tipo: "funil", padrao: true, grupo: "Classificação",
    ajuda: "T, M ou F (Topo, Meio ou Fundo).", exemplo: "T (Topo)" },
  { key: "objetivo", label: "Objetivo", aliases: ["objetivo"], tipo: "texto", textoLongo: true, padrao: true, grupo: "Conteúdo",
    ajuda: "Para que serve a peça.", exemplo: "Mostrar o passo que evita retrabalho." },
  { key: "direcionamento", label: "Direcionamento", aliases: ["direcionamento", "direcionamento do conteudo"], tipo: "texto", textoLongo: true, padrao: true, grupo: "Conteúdo",
    ajuda: "O que a peça diz, como abre e como fecha.", exemplo: "Abre com a dúvida mais comum e fecha com o passo a passo." },
  { key: "descricao", label: "Descrição", aliases: ["descricao", "briefing", "descricao briefing"], tipo: "texto", textoLongo: true, grupo: "Conteúdo",
    ajuda: "Descrição ou briefing do card.", exemplo: "" },
  { key: "legenda", label: "Legenda", aliases: ["legenda", "copy", "texto da legenda"], tipo: "texto", textoLongo: true, grupo: "Conteúdo",
    ajuda: "Legenda do post.", exemplo: "" },
  { key: "observacoes", label: "Observações", aliases: ["observacoes", "observacao", "obs", "notas"], tipo: "texto", textoLongo: true, padrao: true, grupo: "Conteúdo",
    ajuda: "Texto livre. As colunas a mais da planilha entram aqui, depois deste texto.", exemplo: "" },
  { key: "parentCodigo", label: "Código da peça-mãe", aliases: ["codigo da peca mae", "peca mae", "codigo da mae", "mae", "parent codigo", "parent", "vinculo"], tipo: "mae", max: 20, padrao: true, grupo: "Peça relacionada",
    ajuda: "Só para comentário de porta-voz ou story ligado a outro post: o código do post principal. A mãe pode estar nesta planilha ou já ser card no Social Hub.", exemplo: "" },
  { key: "portaVoz", label: "Porta-voz", aliases: ["porta voz", "portavoz"], tipo: "texto", max: 200, padrao: true, grupo: "Peça relacionada",
    ajuda: "Só para comentário de porta-voz: quem assina.", exemplo: "" },
  { key: "anguloComentario", label: "Ângulo do comentário", aliases: ["angulo do comentario", "angulo comentario", "angulo"], tipo: "texto", textoLongo: true, padrao: true, grupo: "Peça relacionada",
    ajuda: "Só para comentário de porta-voz: o que ele vai comentar.", exemplo: "" },
  { key: "linkDrive", label: "Link do Drive", aliases: ["link do drive", "drive", "link drive", "pasta do drive"], tipo: "texto", max: 1000, link: true, grupo: "Links",
    ajuda: "Pasta do Drive do card.", exemplo: "" },
  { key: "linkClickup", label: "Link do ClickUp", aliases: ["link do clickup", "clickup", "link clickup"], tipo: "texto", max: 255, link: true, grupo: "Links",
    ajuda: "Tarefa do ClickUp do card.", exemplo: "" },
];

export function camposDo(tipo: TipoImportacao): CampoDef[] {
  return tipo === "temas" ? CAMPOS_TEMA : CAMPOS_PAUTA;
}
export function campoTitulo(tipo: TipoImportacao): CampoDef {
  return camposDo(tipo).find(c => c.tipo === "titulo")!;
}

// Sugestão de tema não tem etapa (sempre entra em Sugestão de Tema). Coluna
// "Etapa"/"Status" numa planilha de temas é reconhecida só para não poluir
// Observações quando diz "sugestão".
const ALIASES_ETAPA_TEMA = ["etapa", "status", "coluna", "fase"];
const CHAVE_ETAPA_TEMA = "__etapaTema";

// ─── Normalização ──────────────────────────────────────────────────────────

export function normalizar(s: string): string {
  return String(s ?? "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

// Chave de comparação de código: sem acento, sem maiúscula e sem espaço nas
// pontas, a mesma regra da collation do banco (ai_ci). Toda comparação de
// código (repetido na planilha, já usado no banco, peça-mãe) passa por aqui.
export function chaveCodigo(s: unknown): string {
  return String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
}

// Valor de erro do Excel (em inglês, como o leitor devolve, ou em português,
// como vem num CSV do Excel brasileiro).
const ERROS_EXCEL = new RegExp("^#(" + [
  "N/A", "N/D", "VALUE!", "VALOR!", "REF!", "DIV/0!", "NUM!", "NÚM!", "NAME\\?", "NOME\\?", "NULL!", "NULO!",
  "SPILL!", "DESPEJAR!", "CALC!", "GETTING_DATA", "ERRO!?", "ERROR!?",
  "FIELD!", "CAMPO!", "BLOCKED!", "BLOQUEADO!", "CONNECT!", "CONECTAR!", "BUSY!", "OCUPADO!",
  "UNKNOWN!", "DESCONHECIDO!", "PYTHON!", "EXTERNAL!", "EXTERNO!", "TIMEOUT!",
].join("|") + ")$", "i");
// LibreOffice: Err:502, Err:504, Err:508...
const ERROS_LIBREOFFICE = /^Err:5\d\d$/i;
export function ehErroExcel(v: unknown): boolean {
  const s = String(v ?? "").trim();
  return ERROS_EXCEL.test(s) || ERROS_LIBREOFFICE.test(s);
}

// Linha com conteúdo: algum valor que não seja vazio nem erro do Excel, fora das
// colunas ignoradas. Fórmula arrastada que só dá #N/D conta como linha vazia
// (não estoura o limite nem aparece na prévia).
export function linhaTemConteudo(c: string[], ignorar?: number[] | Set<number>): boolean {
  const ign = ignorar instanceof Set ? ignorar : new Set(ignorar ?? []);
  for (let i = 0; i < c.length; i++) {
    if (ign.has(i)) continue;
    const v = String(c[i] ?? "").trim();
    if (v && !ehErroExcel(v)) return true;
  }
  return false;
}

// Limites da entrada (linhas, colunas, tamanho de célula). A tela confere antes
// de mandar e o servidor confere de novo; a mensagem é a mesma nos dois.
// Linhas acima do limite: quantas são e qual coluna, se ignorada sozinha, traz
// a planilha de volta ao limite (linhas que só têm valor naquela coluna, como
// uma coluna de fórmula ou de controle arrastada até o fim).
// - segura: a coluna só tem valor nessas linhas a mais; ignorar não tira nada dos itens;
// - comDado: quantas das linhas que sobram têm valor nela (ignorar tira esse valor de todas);
// - protegida: coluna de título ou de código (nunca sugerida para ignorar).
export interface ColunaDoExcesso { indice: number; cabecalho: string; sobram: number; soNela: number; comDado: number; segura: boolean; protegida: boolean }
export interface ExcessoDeLinhas { cheias: number; colunas: ColunaDoExcesso[] }
type EntradaLimites = { tipo?: TipoImportacao; cabecalhos: string[]; linhas: { c: string[] }[]; ignorar?: number[] };
function colunaProtegida(tipo: TipoImportacao | undefined, cabecalho: string): boolean {
  const tipos: TipoImportacao[] = tipo ? [tipo] : ["temas", "pautas"];
  return tipos.some(t => {
    const c = campoDoCabecalho(t, cabecalho);
    return !!c && (c.obrigatorio || c.tipo === "titulo" || c.tipo === "codigo");
  });
}
export function excessoDeLinhas(entrada: EntradaLimites): ExcessoDeLinhas | null {
  const ign = new Set(entrada.ignorar ?? []);
  let cheias = 0;
  const soNela = new Map<number, number>();
  const total = new Map<number, number>();
  for (const l of entrada.linhas) {
    let unica = -1, qtd = 0;
    for (let i = 0; i < l.c.length; i++) {
      if (ign.has(i)) continue;
      const v = String(l.c[i] ?? "").trim();
      if (v && !ehErroExcel(v)) { qtd++; unica = i; total.set(i, (total.get(i) ?? 0) + 1); }
    }
    if (!qtd) continue;
    cheias++;
    if (qtd === 1) soNela.set(unica, (soNela.get(unica) ?? 0) + 1);
  }
  if (cheias <= LIMITE_LINHAS) return null;
  const colunas = [...soNela.entries()]
    .filter(([, n]) => cheias - n <= LIMITE_LINHAS)
    .map(([indice, n]) => {
      const cabecalho = String(entrada.cabecalhos[indice] ?? "").trim() || `Coluna ${letraColuna(indice)}`;
      const comDado = (total.get(indice) ?? 0) - n;
      return { indice, cabecalho, sobram: cheias - n, soNela: n, comDado, segura: comDado === 0, protegida: colunaProtegida(entrada.tipo, cabecalho) };
    })
    .sort((a, b) => Number(b.segura) - Number(a.segura) || a.sobram - b.sobram);
  return { cheias, colunas };
}

// semDicaDeColuna: a aba foi cortada na leitura; ignorar coluna não resolve, então a dica não aparece.
export function limitesDaEntrada(entrada: EntradaLimites, opcoes: { semDicaDeColuna?: boolean } = {}): string[] {
  const erros: string[] = [];
  const excesso = excessoDeLinhas(entrada);
  if (excesso) {
    let msg = `A planilha tem ${excesso.cheias} linhas preenchidas; o limite é ${LIMITE_LINHAS} por arquivo. Divida em arquivos menores.`;
    if (!opcoes.semDicaDeColuna) {
      const livres = excesso.colunas.filter(c => !c.protegida);
      const segura = livres.find(c => c.segura);
      const comDado = livres.find(c => !c.segura);
      const protegida = excesso.colunas.find(c => c.protegida);
      if (segura) {
        msg += ` Coluna ignorada não conta: a coluna "${segura.cabecalho}" só tem valor em ${segura.soNela} linhas que não têm mais nada; ignorando essa coluna na lista abaixo, ficam ${segura.sobram}.`;
      } else if (comDado) {
        msg += ` As ${comDado.soNela} linhas a mais só têm valor na coluna "${comDado.cabecalho}". Ignorar essa coluna tira o valor dela de todas as linhas (${comDado.comDado} itens têm dado nela); prefira apagar no Excel as linhas a mais.`;
      } else if (protegida) {
        msg += ` As ${protegida.soNela} linhas a mais só têm valor em "${protegida.cabecalho}". Apague essas linhas no Excel.`;
      } else {
        msg += " Coluna ignorada não conta: se uma coluna enche linhas que não são itens, ignore essa coluna na lista abaixo.";
      }
    }
    erros.push(msg);
  }
  // Laço, não spread: com dezenas de milhares de linhas o Math.max(...x) estoura a pilha.
  let largura = entrada.cabecalhos.length;
  for (const l of entrada.linhas) if (l.c.length > largura) largura = l.c.length;
  if (largura > LIMITE_COLUNAS) {
    erros.push(`A planilha tem ${largura} colunas; o limite é ${LIMITE_COLUNAS}. Apague as colunas que não vão subir.`);
  }
  if (entrada.cabecalhos.some(h => String(h ?? "").length > LIMITE_CABECALHO)) {
    erros.push(`Algum cabeçalho passa de ${LIMITE_CABECALHO} caracteres. Confira a linha do cabeçalho.`);
  }
  const grande = entrada.linhas.find(l => l.c.some(v => String(v ?? "").length > LIMITE_CELULA)) as { n?: number; c: string[] } | undefined;
  if (grande) {
    erros.push(`Uma célula${grande.n ? ` da linha ${grande.n}` : ""} passa de ${LIMITE_CELULA.toLocaleString("pt-BR")} caracteres. Diminua o texto antes de subir.`);
  }
  return erros;
}

function chars(s: string): number { return Array.from(s).length; }
function bytes(s: string): number { return new TextEncoder().encode(s).length; }
function limpa(v: unknown): string {
  return String(v ?? "").replace(/\r\n?/g, "\n").trim();
}

// ─── Rótulos (os mesmos da tela) ──────────────────────────────────────────

export const REDES_PAUTA = ["instagram", "linkedin", "tiktok", "youtube", "twitter", "multiplas"] as const;
export const ROTULO_REDE: Record<string, string> = {
  instagram: "Instagram", linkedin: "LinkedIn", tiktok: "TikTok", youtube: "YouTube",
  twitter: "Twitter/X", multiplas: "Múltiplas",
};
export const FORMATOS_PAUTA = ["feed", "reels", "stories", "carrossel", "video", "post", "thread", "comentario"] as const;
export const ROTULO_FORMATO: Record<string, string> = {
  feed: "Feed", reels: "Reels", stories: "Stories", carrossel: "Carrossel", video: "Vídeo",
  post: "Post", thread: "Thread", comentario: "Comentário",
};
export const ROTULO_FUNIL: Record<string, string> = { T: "T (Topo)", M: "M (Meio)", F: "F (Fundo)" };

// ─── Leitura de valores ───────────────────────────────────────────────────

const DIAS_MES = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
function dataValida(y: number, m: number, d: number): boolean {
  if (y < 1900 || y > 2200 || m < 1 || m > 12 || d < 1) return false;
  const bis = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  return d <= (m === 2 && bis ? 29 : DIAS_MES[m - 1]);
}
const p2 = (n: number) => String(n).padStart(2, "0");

// Aceita DD/MM/AAAA (também com - ou .), DD/MM/AA e AAAA-MM-DD (com hora ou não).
// Devolve AAAA-MM-DD, ou null quando vazio, ou "invalida".
export function lerData(v: string): string | null | "invalida" {
  const s = limpa(v);
  if (!s) return null;
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T].*)?$/);
  if (m) {
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return dataValida(y, mo, d) ? `${y}-${p2(mo)}-${p2(d)}` : "invalida";
  }
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})(?:\s.*)?$/);
  if (m) {
    const d = Number(m[1]), mo = Number(m[2]);
    const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    return dataValida(y, mo, d) ? `${y}-${p2(mo)}-${p2(d)}` : "invalida";
  }
  return "invalida";
}

export function formatarDataBR(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso ?? "");
}

export function lerFunil(v: string): "T" | "M" | "F" | null | "invalido" {
  const n = normalizar(v);
  if (!n) return null;
  if (n === "t" || n.startsWith("topo")) return "T";
  if (n === "m" || n.startsWith("meio")) return "M";
  if (n === "f" || n.startsWith("fundo")) return "F";
  return "invalido";
}

const REDE_POR_NOME: Record<string, string> = {
  instagram: "instagram", insta: "instagram", ig: "instagram",
  linkedin: "linkedin", tiktok: "tiktok", youtube: "youtube", yt: "youtube",
  twitter: "twitter", x: "twitter", "twitter x": "twitter",
  multiplas: "multiplas", "multiplas redes": "multiplas", todas: "multiplas",
};
// Rede exata, ou o começo do texto ("LinkedIn Marca" vira LinkedIn, com aviso).
export function lerRede(v: string): { rede: string | null; aproximada: boolean } | "invalida" {
  const n = normalizar(v);
  if (!n) return { rede: null, aproximada: false };
  if (REDE_POR_NOME[n]) return { rede: REDE_POR_NOME[n], aproximada: false };
  const primeira = n.split(" ")[0];
  if (primeira.length > 1 && REDE_POR_NOME[primeira]) return { rede: REDE_POR_NOME[primeira], aproximada: true };
  return "invalida";
}

const FORMATO_POR_NOME: Record<string, string> = {
  feed: "feed", reels: "reels", reel: "reels", stories: "stories", story: "stories",
  carrossel: "carrossel", carousel: "carrossel", video: "video", post: "post",
  thread: "thread", comentario: "comentario",
};
export function lerFormatoPauta(v: string): string | null | "invalido" {
  const n = normalizar(v);
  if (!n) return null;
  if (FORMATO_POR_NOME[n]) return FORMATO_POR_NOME[n];
  if (n.startsWith("coment")) return "comentario";
  return "invalido";
}

// Etapa de card: pelo rótulo do quadro do cliente ou pelo id da etapa.
// Procura primeiro pelo rótulo, depois pelo id. Mais de uma etapa possível é erro.
export function lerEtapa(v: string, quadro: EtapaDef[]): string | null | { fora: string } | { ambigua: string[] } {
  const n = normalizar(v);
  if (!n) return null;
  const etapas = etapasDePauta(quadro);
  const porRotulo = etapas.filter(c => normalizar(c.label) === n);
  if (porRotulo.length > 1) return { ambigua: porRotulo.map(c => c.label) };
  if (porRotulo.length === 1) return porRotulo[0].id;
  const porId = etapas.filter(c => normalizar(c.id) === n);
  if (porId.length > 1) return { ambigua: porId.map(c => c.label) };
  if (porId.length === 1) return porId[0].id;
  return { fora: limpa(v) };
}

export function lerCanais(v: string): string[] {
  return limpa(v).split(/[,;|\n]+/).map(s => s.trim()).filter(Boolean);
}

// ─── Colunas ──────────────────────────────────────────────────────────────

export interface ColunaMapeada {
  indice: number;
  cabecalho: string;          // como veio (vazio vira "Coluna X")
  campo: string | null;       // key do campo, ou null = coluna a mais
  campoLabel: string | null;
  repetida: boolean;          // segundo cabeçalho para o mesmo campo: vira coluna a mais
  destino: "campo" | "observacoes" | "ignorada";
}

export function letraColuna(i: number): string {
  let s = ""; let n = i + 1;
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
  return s;
}

export function campoDoCabecalho(tipo: TipoImportacao, cabecalho: string): CampoDef | null {
  const n = normalizar(cabecalho);
  if (!n) return null;
  for (const c of camposDo(tipo)) {
    if (normalizar(c.label) === n || normalizar(c.key) === n || c.aliases.some(a => normalizar(a) === n)) return c;
  }
  return null;
}

export function mapearColunas(tipo: TipoImportacao, cabecalhos: string[], ignorar: number[] = []): ColunaMapeada[] {
  const usados = new Set<string>();
  const ign = new Set(ignorar);
  return cabecalhos.map((h, indice) => {
    const cabecalho = limpa(h) || `Coluna ${letraColuna(indice)}`;
    let campo: CampoDef | null = limpa(h) ? campoDoCabecalho(tipo, h) : null;
    let chave: string | null = campo?.key ?? null;
    if (!campo && tipo === "temas" && ALIASES_ETAPA_TEMA.includes(normalizar(h))) chave = CHAVE_ETAPA_TEMA;
    let repetida = false;
    if (chave && usados.has(chave)) { repetida = true; campo = null; chave = null; }
    if (chave) usados.add(chave);
    const destino: ColunaMapeada["destino"] = ign.has(indice) ? "ignorada" : chave ? "campo" : "observacoes";
    return {
      indice, cabecalho, campo: chave, repetida, destino,
      campoLabel: campo?.label ?? (chave === CHAVE_ETAPA_TEMA ? "Etapa (sempre Sugestão de Tema)" : null),
    };
  });
}

// ─── Análise do lote ──────────────────────────────────────────────────────

export interface EntradaLote {
  tipo: TipoImportacao;
  cabecalhos: string[];
  linhas: { n: number; c: string[]; avisos?: string[] }[]; // n = número da linha na planilha; avisos da leitura (ex.: hiperlink)
  ignorar?: number[];                    // índices de coluna que a pessoa mandou ignorar
}

// O que já existe no banco, montado pelo servidor (chaves = chaveCodigo(código)).
export interface ContextoLote {
  quadro: EtapaDef[];
  // código → motivo do erro (já usado neste cliente ou no sistema)
  codigosOcupados: Map<string, string>;
  // código → aviso (ex.: sugestão na lixeira com o mesmo código)
  avisosCodigo?: Map<string, string>;
  // mães que já existem no sistema: código → pautaId (null = existe, mas ainda não é card)
  maesNoSistema: Map<string, { pautaId: number | null; titulo: string }>;
}

export interface ItemTema {
  codigo: string; tema: string; dataPub: string | null; semana: string | null; canais: string[];
  formato: string | null; responsavel: string | null; linhaEditorial: string | null; territorio: string | null;
  segmento: string | null; persona: string | null; funil: "T" | "M" | "F" | null;
  objetivo: string | null; direcionamento: string | null; linkReferencia: string | null; observacoes: string | null;
  parentCodigo: string | null; portaVoz: string | null; anguloComentario: string | null;
}
export interface ItemPauta {
  codigo: string | null; titulo: string; coluna: string; rede: string; formato: string;
  dataPrevista: string | null; horarioPostagem: string | null; responsavel: string | null;
  linhaEditorial: string | null; territorio: string | null; segmento: string | null; persona: string | null;
  funil: "T" | "M" | "F" | null; objetivo: string | null; direcionamento: string | null;
  descricao: string | null; legenda: string | null; observacoes: string | null;
  parentCodigo: string | null; portaVoz: string | null; anguloComentario: string | null;
  linkDrive: string | null; linkClickup: string | null;
  // pauta-mãe que já é card (vínculo resolvido); mãe na planilha é ligada ao gravar
  parentIdSistema: number | null;
}

export interface LinhaAnalisada {
  n: number;
  codigo: string | null;
  titulo: string;
  data: string | null;
  etapa: string | null;          // rótulo (cards)
  mae: string | null;
  maeOrigem: "planilha" | "sistema" | null;
  status: "ok" | "erro" | "ignorada";
  erros: string[];
  avisos: string[];
  extras: string[];              // o que vai para Observações ("Coluna: valor")
  links: { campo: string; url: string }[]; // endereço que vai ser gravado em cada campo de link
  item: ItemTema | ItemPauta | null;
}

export interface ResultadoLote {
  tipo: TipoImportacao;
  colunas: ColunaMapeada[];
  linhas: LinhaAnalisada[];
  errosGerais: string[];
  avisosGerais: string[];
  total: number;
  validas: number;
  comErro: number;
  ignoradas: number;
  comVinculo: number;
  etapaInicial: string | null;   // rótulo (cards)
  podeGravar: boolean;           // sem erro geral e com pelo menos uma linha válida
}

// Linha de exemplo do modelo: código EXEMPLO… ou título começando com [EXEMPLO].
export function ehLinhaExemplo(codigo: string, titulo: string): boolean {
  return /^EXEMPLO/i.test(codigo.trim()) || /^\[EXEMPLO\]/i.test(titulo.trim());
}

const SUGESTAO_TEMA = new Set(["", "sugestao", "sugestao de tema", "sugestao tema"]);

export function analisarLote(entrada: EntradaLote, ctx: ContextoLote): ResultadoLote {
  const { tipo } = entrada;
  const campos = camposDo(tipo);
  const porKey = new Map(campos.map(c => [c.key, c]));
  const colunas = mapearColunas(tipo, entrada.cabecalhos, entrada.ignorar ?? []);
  const errosGerais: string[] = [];
  const avisosGerais: string[] = [];
  const tituloDef = campoTitulo(tipo);
  const quadro = ctx.quadro;
  const etapaIni = tipo === "pautas" ? etapaInicial(quadro) : null;

  if (!colunas.some(c => c.campo === tituloDef.key && c.destino === "campo")) {
    errosGerais.push(`A planilha não tem a coluna "${tituloDef.label}". Use o modelo ou dê esse nome à coluna do título.`);
  }
  const ignSet = new Set(entrada.ignorar ?? []);
  const linhasCheias = entrada.linhas.filter(l => linhaTemConteudo(l.c, ignSet));
  errosGerais.push(...limitesDaEntrada(entrada));
  if (!linhasCheias.length) errosGerais.push("A planilha não tem nenhuma linha preenchida abaixo do cabeçalho.");

  const temColuna = (key: string) => colunas.some(x => x.campo === key && x.destino === "campo");
  const linhas: LinhaAnalisada[] = [];
  for (const { n, c, avisos: avisosLeitura } of linhasCheias) {
    const val = (key: string): string => {
      const col = colunas.find(x => x.campo === key && x.destino === "campo");
      return col ? limpa(c[col.indice]) : "";
    };
    const erros: string[] = [];
    const avisos: string[] = (avisosLeitura ?? []).map(a => String(a).slice(0, 500)).slice(0, 10);
    const extras: string[] = [];
    for (const col of colunas) {
      if (col.destino !== "observacoes") continue;
      const v = limpa(c[col.indice]);
      if (v) extras.push(`${col.cabecalho}: ${v}`);
    }
    for (const col of colunas) {
      if (col.destino === "ignorada") continue;
      const v = limpa(c[col.indice]);
      if (ehErroExcel(v)) erros.push(`A coluna "${col.cabecalho}" tem erro do Excel (${v}). Corrija a célula antes de subir.`);
    }
    const codigo = val("codigo");
    const titulo = val(tituloDef.key);
    const linha: LinhaAnalisada = {
      n, codigo: codigo || null, titulo, data: null, etapa: null,
      mae: null, maeOrigem: null, status: "ok", erros, avisos, extras, links: [], item: null,
    };
    linhas.push(linha);
    if (ehLinhaExemplo(codigo, titulo)) {
      linha.status = "ignorada";
      avisos.push("Linha de exemplo do modelo: não entra.");
      continue;
    }

    // Texto: limites de cada campo
    const txt = (key: string): string | null => {
      const def = porKey.get(key)!;
      const v = val(key);
      if (!v) return null;
      if (def.max && chars(v) > def.max) erros.push(`"${def.label}" tem ${chars(v)} caracteres; o limite é ${def.max}.`);
      if (def.textoLongo && bytes(v) > LIMITE_TEXT_BYTES) erros.push(`"${def.label}" passa do tamanho máximo do campo (cerca de 65 mil caracteres).`);
      return v;
    };

    if (!titulo) erros.push(`Falta o ${tituloDef.label.toLowerCase()}.`);
    else txt(tituloDef.key);
    if (codigo) txt("codigo");

    const dataKey = tipo === "temas" ? "dataPub" : "dataPrevista";
    const dataRaw = val(dataKey);
    const data = lerData(dataRaw);
    if (data === "invalida") erros.push(`Data inválida: "${dataRaw}". Use DD/MM/AAAA, por exemplo 05/11/2026.`);
    else linha.data = data;

    const funilRaw = val("funil");
    const funil = lerFunil(funilRaw);
    if (funil === "invalido") erros.push(`Funil "${funilRaw}" não reconhecido. Use T, M ou F (Topo, Meio ou Fundo).`);

    const mae = val("parentCodigo");
    if (mae) {
      txt("parentCodigo");
      linha.mae = mae;
      if (codigo && chaveCodigo(mae) === chaveCodigo(codigo)) {
        erros.push("A peça não pode ser mãe dela mesma.");
      }
    }
    const portaVoz = txt("portaVoz");
    if (portaVoz && !mae) avisos.push("Tem porta-voz, mas não tem código da peça-mãe: o comentário fica solto.");

    const comuns = {
      responsavel: txt("responsavel"), linhaEditorial: txt("linhaEditorial"), territorio: txt("territorio"),
      segmento: txt("segmento"), persona: txt("persona"), funil: funil === "invalido" ? null : funil,
      objetivo: txt("objetivo"), direcionamento: txt("direcionamento"),
      parentCodigo: mae || null, portaVoz, anguloComentario: txt("anguloComentario"),
    };

    // Observações: o texto da coluna, depois as colunas a mais.
    const obsCol = txt("observacoes");
    const observacoes = [obsCol, ...extras].filter(Boolean).join("\n") || null;

    if (tipo === "temas") {
      const etCol = colunas.find(x => x.campo === CHAVE_ETAPA_TEMA && x.destino === "campo");
      let obsFinal = observacoes;
      if (etCol) {
        const v = limpa(c[etCol.indice]);
        if (v && !SUGESTAO_TEMA.has(normalizar(v))) {
          avisos.push(`Sugestão de tema sempre entra em Sugestão de Tema; "${v}" foi para Observações.`);
          const ex = `${etCol.cabecalho}: ${v}`;
          extras.push(ex);
          obsFinal = [obsFinal, ex].filter(Boolean).join("\n");
        }
      }
      if (obsFinal && bytes(obsFinal) > LIMITE_TEXT_BYTES) erros.push("As Observações, somadas às colunas a mais, passam do tamanho máximo do campo.");
      const item: ItemTema = {
        codigo, tema: titulo, dataPub: data === "invalida" ? null : data, semana: txt("semana"),
        canais: lerCanais(val("canais")), formato: txt("formato"),
        ...comuns, linkReferencia: txt("linkReferencia"), observacoes: obsFinal,
      };
      linha.item = item;
    } else {
      if (observacoes && bytes(observacoes) > LIMITE_TEXT_BYTES) erros.push("As Observações, somadas às colunas a mais, passam do tamanho máximo do campo.");
      // Etapa
      const etRaw = val("coluna");
      let coluna = etapaIni!;
      if (etRaw) {
        const nEt = normalizar(etRaw);
        if (nEt === normalizar(ETAPA_SUGESTAO) || nEt.startsWith("sugestao")) {
          erros.push('"Sugestão de Tema" não é etapa de card. Para planejamento, importe como Sugestões de tema.');
        } else {
          const et = lerEtapa(etRaw, quadro);
          if (et && typeof et === "object" && "ambigua" in et) {
            erros.push(`"${etRaw}" pode ser mais de uma etapa do quadro (${et.ambigua.join(", ")}). Ajuste os nomes do quadro deste cliente ou use outra etapa.`);
          } else if (et && typeof et === "object") {
            const lista = etapasDePauta(quadro).map(e => e.label).join(", ");
            erros.push(`A etapa "${et.fora}" não existe no quadro deste cliente. Use uma destas: ${lista}.`);
          } else if (typeof et === "string") {
            if ((COLUNAS_PAUTA_BANCO as readonly string[]).includes(et)) coluna = et;
            else erros.push(`A etapa "${etRaw}" está no quadro, mas o banco não aceita card nela. Fale com o João.`);
          }
        }
      }
      linha.etapa = rotuloEtapa(coluna, quadro);
      // Rede e formato
      const redeRaw = val("rede");
      const rede = lerRede(redeRaw);
      let redeFinal = "instagram";
      if (rede === "invalida") erros.push(`Rede "${redeRaw}" não existe no Social Hub. Use Instagram, LinkedIn, TikTok, YouTube, Twitter/X ou Múltiplas.`);
      else if (rede.rede) {
        redeFinal = rede.rede;
        if (rede.aproximada) avisos.push(`Rede "${redeRaw}" entrou como ${ROTULO_REDE[rede.rede]}.`);
      } else if (temColuna("rede")) avisos.push("Rede vazia: entra como Instagram.");
      const fmtRaw = val("formato");
      const fmt = lerFormatoPauta(fmtRaw);
      let formatoFinal = "feed";
      if (fmt === "invalido") erros.push(`Formato "${fmtRaw}" não existe no card. Use Feed, Reels, Stories, Carrossel, Vídeo, Post, Thread ou Comentário.`);
      else if (fmt) formatoFinal = fmt;
      else if (temColuna("formato")) avisos.push("Formato vazio: entra como Feed.");
      const item: ItemPauta = {
        codigo: codigo || null, titulo, coluna, rede: redeFinal, formato: formatoFinal,
        dataPrevista: data === "invalida" ? null : data, horarioPostagem: txt("horarioPostagem"),
        ...comuns, descricao: txt("descricao"), legenda: txt("legenda"), observacoes,
        linkDrive: txt("linkDrive"), linkClickup: txt("linkClickup"), parentIdSistema: null,
      };
      linha.item = item;
    }
    if (linha.item) {
      const it = linha.item as unknown as Record<string, unknown>;
      linha.links = campos.filter(cd => cd.link && it[cd.key]).map(cd => ({ campo: cd.label, url: String(it[cd.key]) }));
    }
  }

  // Códigos: repetidos na planilha e já existentes no sistema.
  const ativas = linhas.filter(l => l.status !== "ignorada");
  const porCodigo = new Map<string, LinhaAnalisada[]>();
  for (const l of ativas) {
    if (!l.codigo) continue;
    const k = chaveCodigo(l.codigo);
    porCodigo.set(k, [...(porCodigo.get(k) ?? []), l]);
  }
  for (const [k, ls] of porCodigo) {
    if (ls.length > 1) {
      for (const l of ls) {
        const outras = ls.filter(x => x !== l).map(x => x.n).join(", ");
        l.erros.push(`O código ${l.codigo} se repete na planilha (linha ${outras}).`);
      }
    }
    const ocupado = ctx.codigosOcupados.get(k);
    if (ocupado) for (const l of ls) l.erros.push(`Código ${l.codigo}: ${ocupado}`);
    const aviso = ctx.avisosCodigo?.get(k);
    if (aviso) for (const l of ls) l.avisos.push(`Código ${l.codigo}: ${aviso}`);
  }

  // Vínculo com a peça-mãe: na planilha ou no sistema.
  for (const l of ativas) {
    if (!l.mae) continue;
    const k = chaveCodigo(l.mae);
    const naPlanilha = porCodigo.get(k);
    if (naPlanilha && naPlanilha.length) { l.maeOrigem = "planilha"; continue; }
    const sis = ctx.maesNoSistema.get(k);
    if (sis) {
      if (tipo === "pautas" && sis.pautaId == null) {
        l.erros.push(`A peça-mãe ${l.mae} ainda é sugestão de tema. Aprove a mãe antes, ou suba a mãe como card nesta planilha.`);
      } else {
        l.maeOrigem = "sistema";
        if (tipo === "pautas" && l.item) (l.item as ItemPauta).parentIdSistema = sis.pautaId;
      }
      continue;
    }
    l.erros.push(`A peça-mãe ${l.mae} não está na planilha nem no Social Hub.`);
  }
  // Vínculo em círculo dentro da planilha (A é mãe de B e B é mãe de A).
  for (const l of ativas) {
    if (l.maeOrigem !== "planilha" || !l.codigo) continue;
    const caminho = [l.codigo];
    let atual: LinhaAnalisada | undefined = l;
    for (let i = 0; i < 50 && atual?.mae; i++) {
      const prox: LinhaAnalisada | undefined = porCodigo.get(chaveCodigo(atual.mae))?.[0];
      if (!prox) break;
      if (prox === l) { l.erros.push(`Vínculo em círculo: ${[...caminho, l.codigo].join(" > ")}.`); break; }
      caminho.push(prox.codigo ?? "?");
      atual = prox;
    }
  }

  // Status final; filho de mãe com erro também fica de fora.
  for (const l of ativas) l.status = l.erros.length ? "erro" : "ok";
  let mudou = true;
  for (let volta = 0; mudou && volta < 60; volta++) {
    mudou = false;
    for (const l of ativas) {
      if (l.status !== "ok" || l.maeOrigem !== "planilha" || !l.mae) continue;
      const maes = porCodigo.get(chaveCodigo(l.mae)) ?? [];
      const maeErro = maes.find(m => m.status === "erro");
      if (maeErro) {
        l.erros.push(`A peça-mãe ${l.mae} (linha ${maeErro.n}) tem erro e não vai entrar.`);
        l.status = "erro";
        mudou = true;
      }
    }
  }
  const validas = linhas.filter(l => l.status === "ok");
  if (tipo === "pautas") {
    const semRede = validas.filter(l => !colunaTemValor(entrada, colunas, "rede", l.n)).length;
    const semFormato = validas.filter(l => !colunaTemValor(entrada, colunas, "formato", l.n)).length;
    const semEtapa = validas.filter(l => !colunaTemValor(entrada, colunas, "coluna", l.n)).length;
    if (semEtapa) avisosGerais.push(`${semEtapa} ${semEtapa === 1 ? "card sem etapa entra" : "cards sem etapa entram"} em ${rotuloEtapa(etapaIni, quadro)}.`);
    if (semRede) avisosGerais.push(`${semRede} ${semRede === 1 ? "card sem rede entra" : "cards sem rede entram"} como Instagram.`);
    if (semFormato) avisosGerais.push(`${semFormato} ${semFormato === 1 ? "card sem formato entra" : "cards sem formato entram"} como Feed.`);
  }
  const repetidas = colunas.filter(c => c.repetida);
  if (repetidas.length) {
    avisosGerais.push(`Colunas com o mesmo campo de outra: ${repetidas.map(c => `"${c.cabecalho}"`).join(", ")}. Elas vão para Observações.`);
  }

  return {
    tipo, colunas, linhas, errosGerais, avisosGerais,
    total: linhas.length,
    validas: validas.length,
    comErro: linhas.filter(l => l.status === "erro").length,
    ignoradas: linhas.filter(l => l.status === "ignorada").length,
    comVinculo: validas.filter(l => l.maeOrigem).length,
    etapaInicial: etapaIni ? rotuloEtapa(etapaIni, quadro) : null,
    podeGravar: !errosGerais.length && validas.length > 0,
  };
}

function colunaTemValor(entrada: EntradaLote, colunas: ColunaMapeada[], key: string, n: number): boolean {
  const col = colunas.find(x => x.campo === key && x.destino === "campo");
  if (!col) return false;
  const l = entrada.linhas.find(x => x.n === n);
  return !!limpa(l?.c[col.indice]);
}

// Códigos citados na planilha (para o servidor buscar no banco só o necessário).
export function codigosDaEntrada(entrada: EntradaLote): { codigos: string[]; maes: string[] } {
  const colunas = mapearColunas(entrada.tipo, entrada.cabecalhos, entrada.ignorar ?? []);
  const idx = (key: string) => colunas.find(c => c.campo === key && c.destino === "campo")?.indice;
  const ic = idx("codigo"), im = idx("parentCodigo");
  const codigos = new Set<string>(), maes = new Set<string>();
  for (const l of entrada.linhas) {
    if (ic != null) { const v = limpa(l.c[ic]); if (v) codigos.add(v); }
    if (im != null) { const v = limpa(l.c[im]); if (v) maes.add(v); }
  }
  return { codigos: [...codigos], maes: [...maes] };
}

// ─── Exportar itens que já existem (mesmo formato do modelo) ────────────────

// Valor de cada campo como texto de planilha (data DD/MM/AAAA, funil "T (Topo)",
// etapa e rede pelo rótulo).
export function valorParaPlanilha(campo: CampoDef, valor: unknown, quadro: EtapaDef[]): string {
  if (valor == null) return "";
  switch (campo.tipo) {
    case "data": return formatarDataBR(String(valor));
    case "funil": return ROTULO_FUNIL[String(valor)] ?? String(valor);
    case "etapa": return rotuloEtapa(String(valor), quadro);
    case "rede": return ROTULO_REDE[String(valor)] ?? String(valor);
    case "formato": return ROTULO_FORMATO[String(valor)] ?? String(valor);
    case "canais": return Array.isArray(valor) ? valor.join(", ") : String(valor);
    default: return String(valor);
  }
}
